import { Language, LocalStorageKey } from "../data/constants";
import { TranslationKey } from "../translations/TranslationKey";
import { ModuleIndex } from "./Module";

export enum CourseStatus {
  Draft = "Draft",
  Published = "Published",
  Archived = "Archived",
}

export enum CourseCertificateValidity {
  Unlimited = "Unlimited",
  FiveYears = "FiveYears",
}

export enum CourseType {
  None = "None",
  Intermediary = "Intermediary",
  Hve = "Hve",
  ProfessionalOrCicero = "ProfessionalOrCicero",
  Mindeststandard = "Mindeststandard",
}

export const CourseTypeEnumValues = Object.values(CourseType).filter((ct) => ct !== CourseType.None);

export interface CourseIndex {
  id: string;
  nameDE: string;
  nameEN: string;
  nameFR: string;
  nameIT: string;
  certificateValidity: CourseCertificateValidity;
  status: CourseStatus;
}

export interface CourseViewModel {
  id: string;
  nameDE: string;
  nameEN: string;
  nameFR: string;
  nameIT: string;
  certificateValidity: CourseCertificateValidity;
  status: CourseStatus;
  numberOfMandatoryModules: number;
  numberOfSelectionModules: number;
  numberOfProcessModules: number;
  modulesValidity: number | null;
  modulesRelevantForGrade: string[];
  modules: ModuleIndex[];
  type: CourseType;
}

export interface RequiredCourse {
  isNameDEEmpty: boolean;
  isNameFREmpty: boolean;
  isNameENEmpty: boolean;
  isNameITEmpty: boolean;
  isModuleNumbersZero: boolean;
  isModuleListEmpty: boolean;
  isTypeEmpty: boolean;
}

export const CourseStateTranslationKey: {
  [courseStatus in CourseStatus]: TranslationKey;
} = {
  [CourseStatus.Draft]: "common.status.draft",
  [CourseStatus.Published]: "common.status.published",
  [CourseStatus.Archived]: "common.status.archived",
};

export const CourseCertificateValidityTranslationKey: {
  [courseCertificationValidation in CourseCertificateValidity]: TranslationKey;
} = {
  [CourseCertificateValidity.Unlimited]: "course.field.certificateValidity.Unlimited",
  [CourseCertificateValidity.FiveYears]: "course.field.certificateValidity.FiveYears",
};

export const CourseTypeTranslationKey: {
  [courseType in CourseType]: TranslationKey;
} = {
  [CourseType.Mindeststandard]: "course.type.mindeststandard",
  [CourseType.Intermediary]: "course.type.intermediary",
  [CourseType.Hve]: "course.type.hve",
  [CourseType.ProfessionalOrCicero]: "course.type.cicero",
  [CourseType.None]: "examState.none",
};

export const CourseTypeGroupTranslationKey: {
  [courseType in CourseType]: TranslationKey;
} = {
  [CourseType.Mindeststandard]: "course.type.mindeststandardGroup",
  [CourseType.Intermediary]: "course.type.intermediaryGroup",
  [CourseType.Hve]: "course.type.hveGroup",
  [CourseType.ProfessionalOrCicero]: "course.type.ciceroGroup",
  [CourseType.None]: "examState.none",
};

export const GetLocalizedName = (course: CourseIndex): string => {
  const language = localStorage.getItem(LocalStorageKey.LastSelectedLanguageKey);

  switch (language) {
    case Language.EN:
      return course.nameEN;
    case Language.FR:
      return course.nameFR;
    case Language.IT:
      return course.nameIT;
  }

  return course.nameDE;
};
